<template>
  <div :class="['create-product-page', { 'create-product-page--dark': isDarkTheme }]">
    <Card>
      <div class="mb-3">
        <Button
          noMargin
          @click="
            $router.push(editMode ? '/products/' + productEditId : '/products')
          "
        >
          <i class="fas fa-chevron-left"></i>
          {{ editMode ? "Back To Product" : "Back To Product List" }}
        </Button>
      </div>
      <h3 class="page-title">
        {{ editMode ? "Add Product Variants" : "Create New Product" }}
      </h3>
      <div v-if="editBlockedReason" class="edit-blocked-message" role="alert">
        <i class="fas fa-lock" aria-hidden="true"></i>
        <div>
          <strong>Variations cannot be added</strong>
          <span>{{ editBlockedReason }}</span>
        </div>
      </div>
      <section v-else class="product-kind-step" aria-labelledby="product-kind-heading">
        <div class="step-heading">
          <span class="step-number">1</span>
          <div>
            <h4 id="product-kind-heading" class="section-title mb-1">
              {{ editMode ? "Existing master product type" : "Choose the product type" }}
            </h4>
            <p class="text-muted mb-0">
              This controls inventory behaviour and which product specifications are shown.
            </p>
          </div>
        </div>
        <div class="product-kind-grid" role="radiogroup" aria-labelledby="product-kind-heading">
          <button
            v-for="option in productKindOptions"
            :key="option.id"
            type="button"
            :class="['product-kind-card', { 'product-kind-card--selected': productKind === option.id }]"
            role="radio"
            :aria-checked="productKind === option.id ? 'true' : 'false'"
            :aria-disabled="editMode ? 'true' : 'false'"
            :disabled="editMode"
            @click="selectProductKind(option.id)"
          >
            <i :class="option.icon" aria-hidden="true"></i>
            <span>
              <strong>{{ option.title }}</strong>
              <small>{{ option.description }}</small>
            </span>
            <i v-if="productKind === option.id" class="fas fa-check-circle selection-check" aria-hidden="true"></i>
          </button>
        </div>
        <p v-if="editMode && productKind" class="selection-guidance mb-0" role="status">
          {{ productKindTitle }} is derived from the existing master product and cannot be changed while adding variations.
        </p>
      </section>

      <div v-if="!editBlockedReason && productKind && (!editMode || productKindConfirmed)" class="product-workflow">
        <div class="step-heading step-heading--details">
          <span class="step-number">2</span>
          <div>
            <h4 class="section-title mb-1">Product details</h4>
            <p class="text-muted mb-0">Creating a {{ productKindTitle.toLowerCase() }} product.</p>
          </div>
        </div>
        <div v-if="!editMode" class="row">
          <div class="col-md-6">
            <CustomInput
              v-model="itemName"
              placeholder="Enter name"
              type="text"
              label="Product Name*"
            />
          </div>
          <!-- <div class="col-md-6">
                        <CustomInput
                            v-model="itemDescription"
                            placeholder="Enter short description"
                            type="text"
                            label="Short Description*"
                        />
                    </div> -->
          <div class="col-md-6">
            <SelectDropdown
              normal
              label="Category*"
              v-if="categoriesLoaded"
              :options="
                categories.map((item) => ({ id: item.name, title: item.name }))
              "
              @change="handleCategorySelect"
            />
          </div>
          <div class="col-md-6">
            <CustomInput
              v-model="manufacturerName"
              placeholder="Enter Manufacturer Name"
              type="text"
              label="Manufacturer Name"
            />
          </div>
          <div class="col-md-6">
            <CustomInput
              v-model="brandName"
              placeholder="Enter Brand Name"
              type="text"
              label="Brand Name"
            />
          </div>
          <div v-if="isCannabis" class="col-md-6">
            <CustomInput
              v-model="strainName"
              placeholder="Enter Strain (e.g., Sativa, Indica)"
              type="text"
              label="Strain"
            />
          </div>
        </div>
        <div v-else-if="editProduct">
          <div class="my-3">
            <div><strong>Product Name</strong></div>
            <div>{{ editProduct.name }}</div>
          </div>
          <div class="my-3">
            <div><strong>Category</strong></div>
            <div>{{ editProduct.item_group }}</div>
          </div>
          <div class="my-3" v-if="editProduct.brand">
            <div><strong>Brand</strong></div>
            <div>{{ editProduct.brand }}</div>
          </div>
          <div class="my-3" v-if="editProduct.strain">
            <div><strong>Strain</strong></div>
            <div>{{ editProduct.strain }}</div>
          </div>
        </div>

        <section class="master-asset mt-3" aria-labelledby="master-asset-heading">
          <h4 id="master-asset-heading" class="section-title">Master product image</h4>
          <p class="text-muted">
            {{ editMode
              ? "The existing master image is shown for reference. New variations can use their own images below."
              : "Upload the main catalog image. It will use the same product asset path as existing product images." }}
          </p>
          <div class="asset-control">
            <img
              v-if="masterImagePreview"
              :src="masterImagePreview"
              class="asset-preview"
              alt="Selected master product preview"
            />
            <img
              v-else-if="editMode && editProduct && editProduct.image"
              :src="getServerAssetUrl(editProduct.image)"
              class="asset-preview"
              alt="Current master product"
            />
            <div v-else class="asset-placeholder" aria-hidden="true">
              <i class="fas fa-image"></i>
            </div>
            <div class="asset-copy">
              <label v-if="!editMode" for="master-product-image" class="file-label">Choose master image</label>
              <input
                v-if="!editMode"
                id="master-product-image"
                class="form-control-file"
                type="file"
                accept=".jpg,.jpeg,.png,.webp,.gif,image/jpeg,image/png,image/webp,image/gif"
                @change="handleMasterImage"
              />
              <div v-if="masterImage" class="file-metadata" aria-live="polite">
                <strong>{{ masterImage.name }}</strong>
                <span>{{ masterImage.type || "Image" }} · {{ formatFileSize(masterImage) }}</span>
              </div>
              <div v-else-if="editMode && editProduct && editProduct.image" class="file-metadata">
                <strong>Current asset</strong>
                <span>{{ editProduct.image }}</span>
              </div>
              <small v-else class="text-muted">PNG, JPEG, WebP, or GIF within the server image upload limit.</small>
            </div>
          </div>
        </section>

        <section v-if="isCannabis" class="master-specifications mt-3" aria-labelledby="master-specifications-heading">
          <h4 id="master-specifications-heading" class="section-title">Master cannabis specifications</h4>
          <p class="text-muted">Use the master fields for shared catalog context. Each variation can record its own measured values below.</p>
          <div v-if="!editMode" class="row">
            <div class="col-md-4 mb-3">
              <label>Retail Marijuana Product Type</label>
              <SelectDropdown
                normal
                :value="masterRetailMarijuanaProductType"
                :options="marijuanaProductType.map((item) => ({ id: item.title, title: item.title }))"
                @change="masterRetailMarijuanaProductType = $event"
              />
            </div>
            <div class="col-md-4 mb-3">
              <label>Health Canada Reporting</label>
              <SelectDropdown
                normal
                :value="masterHealthCanadaReportingCategory"
                :options="healthCanadaReportingCategory.map((item) => ({ id: item.title, title: item.title }))"
                @change="masterHealthCanadaReportingCategory = $event"
              />
            </div>
            <div class="col-md-4 mb-3">
              <label for="master-equivalent-to">Equivalent To (g)</label>
              <input id="master-equivalent-to" v-model="masterEquivalentTo" type="number" min="0" step="0.001" class="form-control" />
            </div>
            <div class="col-12 mb-3">
              <label>Terpenes</label>
              <SelectDropdown
                :value="masterTerpenes"
                :multiple="true"
                :options="terpenesMaster.map((item) => ({ id: item.id, title: item.name || item.title }))"
                @change="masterTerpenes = $event || []"
              />
              <label for="master-terpene-paste" class="paste-label">Paste terpene list from OCS</label>
              <textarea
                id="master-terpene-paste"
                v-model="masterTerpenePaste"
                class="form-control"
                rows="2"
                placeholder="Limonene, Myrcene, Caryophyllene"
              ></textarea>
              <button type="button" class="link-btn paste-terpenes-btn" @click="applyMasterTerpenePaste">
                Add pasted terpenes
              </button>
              <div v-if="masterTerpenes.length" class="terpene-token-summary" aria-live="polite">
                <span v-for="terpene in masterTerpenes" :key="String(terpene)">{{ terpeneLabel(terpene) }}</span>
              </div>
            </div>
          </div>
          <div v-if="!editMode" class="potency-grid">
            <label>THC minimum (%)<input v-model="masterThcMin" type="number" min="0" step="0.01" class="form-control" /></label>
            <label>THC maximum (%)<input v-model="masterThcMax" type="number" min="0" step="0.01" class="form-control" /></label>
            <label>CBD minimum (%)<input v-model="masterCbdMin" type="number" min="0" step="0.01" class="form-control" /></label>
            <label>CBD maximum (%)<input v-model="masterCbdMax" type="number" min="0" step="0.01" class="form-control" /></label>
          </div>
          <dl v-else class="spec-summary">
            <div><dt>Product type</dt><dd>{{ masterRetailMarijuanaProductType || "Not set" }}</dd></div>
            <div><dt>Reporting category</dt><dd>{{ masterHealthCanadaReportingCategory || "Not set" }}</dd></div>
            <div><dt>Equivalent to</dt><dd>{{ masterEquivalentTo || "Not set" }}</dd></div>
            <div><dt>Terpenes</dt><dd>{{ masterTerpenes.length ? masterTerpenes.join(", ") : "Not set" }}</dd></div>
          </dl>
        </section>

        <section class="master-descriptions mt-3" aria-labelledby="master-descriptions-heading">
          <h4 id="master-descriptions-heading" class="section-title">
            Master descriptions
          </h4>
          <p v-if="editMode" class="text-muted">
            These descriptions come from the existing master product. Use the copy buttons to apply either value to the new variations.
          </p>
          <div class="row">
            <div class="col-md-6 mb-3">
              <label for="master-short-description">Master Short Description</label>
              <textarea
                id="master-short-description"
                v-model="itemDescription"
                class="form-control"
                rows="3"
                :readonly="editMode"
              ></textarea>
              <button
                type="button"
                class="link-btn copy-description-btn mt-2"
                @click="copyShortDescriptionToVariations"
              >
                Copy short description to all variations
              </button>
            </div>
            <div class="col-md-6 mb-3">
              <label for="master-long-description">Master Long Description</label>
              <textarea
                id="master-long-description"
                v-model="itemLongDescription"
                class="form-control"
                rows="5"
                :readonly="editMode"
              ></textarea>
              <button
                type="button"
                class="link-btn copy-description-btn mt-2"
                @click="copyLongDescriptionToVariations"
              >
                Copy long description to all variations
              </button>
            </div>
          </div>
        </section>

        <div class="mt-4 mb-0 product-variations">
          <div v-for="(variation, i) of variations" :key="variation.id" class="variation-panel mb-3">
            <div class="variation-header d-flex justify-content-between align-items-center">
              <h5 class="mb-0">
                <span v-if="variations[i].name">{{ variations[i].name }}</span>
                <span v-else>New Variation {{ i + 1 }}</span>
              </h5>
              <div>
                <button @click="removeVariant(variation)" class="link-btn text-danger">
                  <i class="fas fa-trash"></i> Remove
                </button>
                <button @click="toggleVariation(i)" class="link-btn ml-2">
                  <i :class="['fas', expandedVariations[i] ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
                </button>
              </div>

            </div>
            
            <div v-if="expandedVariations[i]" class="variation-content">
              <div class="row">
                <div class="col-md-6 mb-2">
                  <label>Variation Name*</label>
                  <input type="text" class="form-control" v-model="variations[i].name" />
                </div>
                
                <div class="col-md-3 mb-2">
                  <label>Price</label>
                  <input type="number" class="form-control" v-model="variations[i].selling_price" />
                </div>
                
                <div class="col-md-3 mb-2">
                  <label>Size</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="variations[i].weight"
                    placeholder="e.g. 3.5g, 1oz, Mini"
                  />
                </div>
              </div>

              <section class="variation-asset mb-3" :aria-labelledby="`variation-asset-heading-${variation.id}`">
                <h6 :id="`variation-asset-heading-${variation.id}`" class="subsection-title">Variation image</h6>
                <div class="asset-control asset-control--compact">
                  <img
                    v-if="variation.image_preview"
                    :src="variation.image_preview"
                    class="asset-preview"
                    :alt="`${variation.name || `Variation ${i + 1}`} preview`"
                  />
                  <div v-else class="asset-placeholder" aria-hidden="true"><i class="fas fa-image"></i></div>
                  <div class="asset-copy">
                    <label :for="`variation-image-${variation.id}`" class="file-label">Choose variation image</label>
                    <input
                      :id="`variation-image-${variation.id}`"
                      type="file"
                      class="form-control-file"
                      accept=".jpg,.jpeg,.png,.webp,.gif,image/jpeg,image/png,image/webp,image/gif"
                      @change="handleVariationImage(i, $event)"
                    />
                    <div v-if="variation.image_file" class="file-metadata" aria-live="polite">
                      <strong>{{ variation.image_file.name }}</strong>
                      <span>{{ variation.image_file.type || "Image" }} · {{ formatFileSize(variation.image_file) }}</span>
                    </div>
                    <small v-else class="text-muted">Optional. This image is stored on this variation only.</small>
                  </div>
                </div>
              </section>

              <div class="row variation-descriptions">
                <div class="col-md-6 mb-2">
                  <label :for="`variation-short-description-${variation.id}`">Short Description</label>
                  <textarea
                    :id="`variation-short-description-${variation.id}`"
                    v-model="variations[i].short_description"
                    class="form-control"
                    rows="3"
                  ></textarea>
                </div>
                <div class="col-md-6 mb-2">
                  <label :for="`variation-long-description-${variation.id}`">Long Description</label>
                  <textarea
                    :id="`variation-long-description-${variation.id}`"
                    v-model="variations[i].description"
                    class="form-control"
                    rows="5"
                  ></textarea>
                </div>
              </div>

              <div class="row variation-additional-info">
                <div class="col-12 mb-3">
                  <label :for="`variation-notes-${variation.id}`">Additional information / notes</label>
                  <textarea
                    :id="`variation-notes-${variation.id}`"
                    v-model="variations[i].notes"
                    class="form-control"
                    rows="3"
                    placeholder="Package, merchandising, or catalog details specific to this variation"
                  ></textarea>
                </div>
              </div>
              
              <div v-if="isCannabis" class="cannabis-variation-specifications">
                <h6 class="subsection-title">Cannabis specifications</h6>
                <div class="row">
                <div class="col-md-6 mb-2">
                  <label>Retail Marijuana Product Type</label>
                  <SelectDropdown
                    normal
                    :value="variations[i].retail_marijuana_product_type__canada"
                    :options="
                      marijuanaProductType.map((item) => ({
                        id: item.title,
                        title: item.title,
                      }))
                    "
                    @change="
                      variations[i].retail_marijuana_product_type__canada = $event
                    "
                  />
                </div>
                
                <div class="col-md-3 mb-2">
                  <label>Equivalent To</label>
                  <input type="number" class="form-control" v-model="variations[i].equivalent_to" />
                </div>
                
                <div class="col-md-3 mb-2">
                  <label>Health Canada Reporting</label>
                  <SelectDropdown
                    normal
                    :value="variations[i].health_canada_reporting_category"
                    :options="
                      healthCanadaReportingCategory.map((item) => ({
                        id: item.title,
                        title: item.title,
                      }))
                    "
                    @change="
                      variations[i].health_canada_reporting_category = $event
                    "
                  />
                </div>
                </div>
                <div class="row">
                  <div class="col-12 mb-3">
                    <label>Terpenes</label>
                    <SelectDropdown
                      :value="variations[i].terpenes"
                      :multiple="true"
                      :options="terpenesMaster.map((item) => ({ id: item.id, title: item.name || item.title }))"
                      @change="variations[i].terpenes = $event || []"
                    />
                    <label :for="`variation-terpene-paste-${variation.id}`" class="paste-label">Paste terpene list from OCS</label>
                    <textarea
                      :id="`variation-terpene-paste-${variation.id}`"
                      v-model="variations[i].terpene_paste"
                      class="form-control"
                      rows="2"
                      placeholder="Limonene, Myrcene, Caryophyllene"
                    ></textarea>
                    <button type="button" class="link-btn paste-terpenes-btn" @click="applyVariationTerpenePaste(i)">
                      Add pasted terpenes
                    </button>
                    <div v-if="variations[i].terpenes.length" class="terpene-token-summary" aria-live="polite">
                      <span v-for="terpene in variations[i].terpenes" :key="String(terpene)">{{ terpeneLabel(terpene) }}</span>
                    </div>
                  </div>
                </div>
                <div class="potency-grid mb-3">
                  <label>THC minimum (%)<input v-model="variations[i].thc_min" type="number" min="0" step="0.01" class="form-control" /></label>
                  <label>THC maximum (%)<input v-model="variations[i].thc_max" type="number" min="0" step="0.01" class="form-control" /></label>
                  <label>CBD minimum (%)<input v-model="variations[i].cbd_min" type="number" min="0" step="0.01" class="form-control" /></label>
                  <label>CBD maximum (%)<input v-model="variations[i].cbd_max" type="number" min="0" step="0.01" class="form-control" /></label>
                </div>
              </div>
              
              <div class="row">
                <div class="col-md-3 mb-2">
                  <label>Inventory behaviour</label>
                  <div class="readonly-behaviour">
                    {{ isNonStocked ? "Non-stocked" : "Stocked" }}
                  </div>
                </div>
                
                <div class="col-md-3 mb-2">
                  <label>Unit of Measure</label>
                  <SelectDropdown
                    normal
                    :options="
                      weightUoms.map((item) => ({
                        id: item.title,
                        title: item.title,
                      }))
                    "
                    :value="variations[i].weight_uom"
                    @change="variations[i].weight_uom = $event"
                  />
                </div>
                
                <div class="col-md-3 mb-2">
                  <label :for="`variation-batch-tracking-${variation.id}`">Batch Tracking</label>
                  <select
                    v-if="!isNonStocked"
                    :id="`variation-batch-tracking-${variation.id}`"
                    class="form-control"
                    :value="variations[i].batch_tracking"
                    @change="handleBatchTrackingChange(i, $event.target.value)"
                  >
                    <option :value="1">Yes</option>
                    <option :value="0">No</option>
                  </select>
                  <div v-else class="readonly-behaviour">Off</div>
                  <small v-if="isNonStocked" class="text-muted">Not available for gift cards or services.</small>
                </div>
                
                <div class="col-md-3 mb-2">
                  <label>Supplier</label>
                  <SelectDropdown
                    normal
                    :options="
                      suppliers.map((item) => ({
                        id: item.id,
                        title: item.name,
                      }))
                    "
                    :value="variations[i].supplier_sku_name"
                    @change="variations[i].supplier_sku_name = $event"
                  />
                </div>
              </div>
              
              <div class="row">
                <div class="col-md-4 mb-2">
                  <label>Supplier SKU</label>
                  <input type="text" class="form-control" v-model="variations[i].supplier_sku" />
                </div>
                
                <div class="col-md-4 mb-2">
                  <label>UPC</label>
                  <input type="text" class="form-control" v-model="variations[i].upc" />
                </div>
                
                <div class="col-md-4 mb-2">
                  <label>Manufacturer SKU</label>
                  <input type="text" class="form-control" v-model="variations[i].manufacturer_sku" />
                </div>
              </div>
            </div>
          </div>
          
          <div class="mb-3">
            <a @click="addVariation" href="javascript:void(0)" class="link-btn">
              <i class="fas fa-plus"></i> Add Another Variation
            </a>
          </div>
        </div>
        <div class="d-flex justify-content-end">
          <Button @click="createProduct" noMargin>{{
            processingForm
              ? "Please wait..."
              : editMode
              ? "Create Variants"
              : "Create Product"
          }}</Button>
        </div>
      </div>
    </Card>
  </div>
</template>

<script>
import { http } from "@/utils/http";
import CustomInput from "../../components/crud/custom-input.vue";
import SelectDropdown from "../../components/crud/SelectDropdown.vue";
import Button from "../../components/UI/Button.vue";
import { mapState } from "vuex";
import Card from "../../components/UI/Card.vue";
import { getServerAssetUrl } from "../../utils/common";

const PRODUCT_KINDS = {
  CANNABIS: "CANNABIS",
  NON_CANNABIS: "NON_CANNABIS",
  NON_STOCKED: "NON_STOCKED",
};

const PRODUCT_KIND_OPTIONS = [
  {
    id: PRODUCT_KINDS.CANNABIS,
    title: "Cannabis",
    description: "Inventory-tracked cannabis with regulatory specifications and batch tracking enabled by default.",
    icon: "fas fa-cannabis",
  },
  {
    id: PRODUCT_KINDS.NON_CANNABIS,
    title: "Non-Cannabis",
    description: "Accessories and other stocked goods. Batch tracking starts off and can be turned on explicitly.",
    icon: "fas fa-box",
  },
  {
    id: PRODUCT_KINDS.NON_STOCKED,
    title: "Non-Stocked",
    description: "Gift cards and services that do not create stock or batch movements.",
    icon: "fas fa-gift",
  },
];

export default {
  data() {
    return {
      itemName: "",
      productKind: "",
      productKindConfirmed: false,
      productKindOptions: PRODUCT_KIND_OPTIONS,
      loading: false,
      itemGroups: [],
      categoriesLoaded: false,
      categories: [],
      selectedCategory: "",
      manufacturerName: "",
      brandName: "", // New field for product brand
      strainName: "", // New field for product strain
      variations: [],
      nextVariationId: 1,
      expandedVariations: [true], // Track which variations are expanded
      processingForm: false,
      itemDescription: "",
      itemLongDescription: "",
      masterImage: null,
      masterImagePreview: "",
      masterRetailMarijuanaProductType: "",
      masterEquivalentTo: "",
      masterHealthCanadaReportingCategory: "",
      masterTerpenes: [],
      masterTerpenePaste: "",
      masterThcMin: "",
      masterThcMax: "",
      masterCbdMin: "",
      masterCbdMax: "",
      editMode: false,
      productEditId: "",
      editProduct: null,
      editProductVariants: [],
      editBlockedReason: "",
    };
  },
  components: {
    Card,
    CustomInput,
    SelectDropdown,
    Button,
  },
  async mounted() {
    if (this.$route.query.product_id) {
      this.editMode = true;
      this.productEditId = this.$route.query.product_id;
    }
    await this.fetchCategories();
    if (this.editMode) {
      await this.fetchProduct();
    }
  },
  beforeDestroy() {
    this.revokePreview(this.masterImagePreview);
    this.variations.forEach((variation) => this.revokePreview(variation.image_preview));
  },
  computed: {
    ...mapState("misc", [
      "healthCanadaReportingCategory",
      "marijuanaProductType",
      "weightUoms",
      "suppliers",
      "terpenesMaster",
      "theme"
    ]),
    isCannabis() {
      return this.productKind === PRODUCT_KINDS.CANNABIS;
    },
    isNonStocked() {
      return this.productKind === PRODUCT_KINDS.NON_STOCKED;
    },
    productKindTitle() {
      const option = this.productKindOptions.find((item) => item.id === this.productKind);
      return option ? option.title : "";
    },
    isDarkTheme() {
      return String(this.theme || "").toUpperCase() === "DARK";
    },
  },
  methods: {
    getServerAssetUrl,
    revokePreview(preview) {
      if (preview && preview.startsWith("blob:") && window.URL && window.URL.revokeObjectURL) {
        window.URL.revokeObjectURL(preview);
      }
    },
    createImagePreview(file) {
      return file && window.URL && window.URL.createObjectURL
        ? window.URL.createObjectURL(file)
        : "";
    },
    formatFileSize(file) {
      if (!file || !Number.isFinite(file.size)) return "";
      if (file.size < 1024) return `${file.size} B`;
      if (file.size < 1024 * 1024) return `${Math.round(file.size / 1024)} KB`;
      return `${(file.size / (1024 * 1024)).toFixed(1)} MB`;
    },
    handleMasterImage(event) {
      const file = event.target.files && event.target.files[0];
      this.revokePreview(this.masterImagePreview);
      this.masterImage = file || null;
      this.masterImagePreview = this.createImagePreview(file);
    },
    handleVariationImage(index, event) {
      const file = event.target.files && event.target.files[0];
      const variation = this.variations[index];
      this.revokePreview(variation.image_preview);
      this.$set(variation, "image_file", file || null);
      this.$set(variation, "image_preview", this.createImagePreview(file));
    },
    inferProductKind(product, variants = []) {
      const allItems = [product, ...variants].filter(Boolean);
      if (product && (product.non_stock == 1 || product.is_stock_item == 0)) {
        return PRODUCT_KINDS.NON_STOCKED;
      }
      if (product && product.batch_tracking == 1) return PRODUCT_KINDS.CANNABIS;
      const cannabisFields = [
        "retail_marijuana_product_type__canada",
        "health_canada_reporting_category",
        "terpenes",
        "thc_min",
        "thc_max",
        "cbd_min",
        "cbd_max",
      ];
      return allItems.some((item) => cannabisFields.some((field) => item[field] !== null && item[field] !== undefined && item[field] !== ""))
        ? PRODUCT_KINDS.CANNABIS
        : PRODUCT_KINDS.NON_CANNABIS;
    },
    selectProductKind(kind) {
      if (this.editMode) return;
      if (kind === this.productKind) {
        this.productKindConfirmed = true;
        return;
      }
      const hasExplicitBatchTracking = this.variations.some(
        (variation) => variation._batch_tracking_touched && variation.batch_tracking
      );
      if (
        kind === PRODUCT_KINDS.NON_STOCKED &&
        hasExplicitBatchTracking &&
        !window.confirm("Non-stocked products cannot use batch tracking. Continue and turn batch tracking off for every variation?")
      ) {
        return;
      }

      this.productKind = kind;
      this.productKindConfirmed = true;
      if (!this.variations.length) {
        this.variations.push(this.createVariantObj());
        this.$set(this.expandedVariations, 0, true);
        return;
      }
      this.variations.forEach((variation) => this.applyProductKindDefaults(variation));
    },
    applyProductKindDefaults(variation) {
      const nonStocked = this.productKind === PRODUCT_KINDS.NON_STOCKED;
      this.$set(variation, "non_stock", nonStocked ? 1 : 0);
      this.$set(variation, "is_stock_item", nonStocked ? 0 : 1);
      if (nonStocked) {
        this.$set(variation, "batch_tracking", 0);
        return;
      }
      if (!variation._batch_tracking_touched) {
        this.$set(
          variation,
          "batch_tracking",
          this.productKind === PRODUCT_KINDS.CANNABIS ? 1 : 0
        );
      }
    },
    handleBatchTrackingChange(index, value) {
      const variation = this.variations[index];
      const enabled = value === true || value === 1 || value === "1";
      this.$set(variation, "batch_tracking", enabled ? 1 : 0);
      this.$set(variation, "_batch_tracking_touched", true);
    },
    parsePastedTerpenes(value) {
      const existingOptions = this.terpenesMaster || [];
      const tokens = String(value || "")
        .replace(/^\s*terpenes?\s*:\s*/i, "")
        .split(/[\n,;|]+/)
        .map((token) => token.replace(/^\s*[-•]\s*/, "").trim())
        .filter(Boolean);
      return tokens.map((token) => {
        const normalized = token.toLowerCase();
        const match = existingOptions.find((option) =>
          [option.id, option.name, option.title]
            .filter((item) => item !== undefined && item !== null)
            .some((item) => String(item).trim().toLowerCase() === normalized)
        );
        return match ? match.id : token;
      });
    },
    mergeTerpeneSelections(current, pasted) {
      const next = [...(Array.isArray(current) ? current : []), ...this.parsePastedTerpenes(pasted)];
      const seen = new Set();
      return next.filter((value) => {
        const key = String(value).trim().toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    applyMasterTerpenePaste() {
      this.masterTerpenes = this.mergeTerpeneSelections(this.masterTerpenes, this.masterTerpenePaste);
      this.masterTerpenePaste = "";
    },
    applyVariationTerpenePaste(index) {
      const variation = this.variations[index];
      this.$set(
        variation,
        "terpenes",
        this.mergeTerpeneSelections(variation.terpenes, variation.terpene_paste)
      );
      this.$set(variation, "terpene_paste", "");
    },
    terpeneLabel(value) {
      const match = (this.terpenesMaster || []).find((option) => option.id == value);
      return match ? match.name || match.title : value;
    },
    toggleVariation(index) {
      this.$set(this.expandedVariations, index, !this.expandedVariations[index]);
    },
    async fetchProduct() {
      this.loading = true;
      this.editBlockedReason = "";
      try {
        const res = await http.get(`/items/${this.productEditId}`);
        this.editProduct = res.data.result;
        this.editProductVariants = res.data.variants || [];
        if (!this.editProduct || this.editProduct.parent || this.editProduct.variant_of) {
          this.editBlockedReason = "Only a master product can receive new variations.";
        } else {
          const status = String(this.editProduct.product_status || "").trim().toLowerCase();
          const disabled = [true, 1, "1", "true"].includes(this.editProduct.disabled);
          if (status === "archived" || disabled) {
            this.editBlockedReason = "Reactivate the master product before adding variations.";
          } else {
            this.itemDescription = this.editProduct.short_description || "";
            this.itemLongDescription = this.editProduct.description || "";
            this.productKind = this.inferProductKind(this.editProduct, this.editProductVariants);
            this.productKindConfirmed = true;
            this.masterRetailMarijuanaProductType = this.editProduct.retail_marijuana_product_type__canada || "";
            this.masterEquivalentTo = this.editProduct.equivalent_to || "";
            this.masterHealthCanadaReportingCategory = this.editProduct.health_canada_reporting_category || "";
            this.masterTerpenes = String(this.editProduct.terpenes || "").split(",").filter(Boolean);
            this.masterThcMin = this.editProduct.thc_min || "";
            this.masterThcMax = this.editProduct.thc_max || "";
            this.masterCbdMin = this.editProduct.cbd_min || "";
            this.masterCbdMax = this.editProduct.cbd_max || "";
            if (!this.variations.length) {
              this.variations.push(this.createVariantObj());
              this.$set(this.expandedVariations, 0, true);
            }
          }
        }
      } catch (err) {
        console.log("error in loading items", err);
        this.editBlockedReason = "The master product could not be loaded. Return to the product detail and try again.";
      }
      this.loading = false;
    },
    createVariantObj() {
      const variationId = this.nextVariationId;
      this.nextVariationId += 1;
      return {
        id: variationId,
        name: "",
        selling_price: "",
        weight: "",
        catalog_sku: "",
        supplier_sku: "",
        manufacturer_sku: "",
        upc: "",
        short_description: "",
        description: "",
        notes: "",
        image_file: null,
        image_preview: "",
        retail_marijuana_product_type__canada: "",
        equivalent_to: "",
        health_canada_reporting_category: "",
        terpenes: [],
        terpene_paste: "",
        thc_min: "",
        thc_max: "",
        cbd_min: "",
        cbd_max: "",
        non_stock: this.isNonStocked ? 1 : 0,
        is_stock_item: this.isNonStocked ? 0 : 1,
        batch_tracking: this.isCannabis ? 1 : 0,
        _batch_tracking_touched: false,
      };
    },
    handleCategorySelect(e) {
      console.log("handle category select.", e);
      this.selectedCategory = e;
    },
    addVariation() {
      this.variations.push(this.createVariantObj());
      this.$set(this.expandedVariations, this.variations.length - 1, true);
    },
    copyShortDescriptionToVariations() {
      if (!this.confirmDescriptionCopy("short")) return;
      this.variations.forEach((variation) => {
        this.$set(variation, "short_description", this.itemDescription);
      });
    },
    copyLongDescriptionToVariations() {
      if (!this.confirmDescriptionCopy("long")) return;
      this.variations.forEach((variation) => {
        this.$set(variation, "description", this.itemLongDescription);
      });
    },
    confirmDescriptionCopy(descriptionType) {
      const count = this.variations.length;
      return window.confirm(
        `Replace the ${descriptionType} description on all ${count} variation${count === 1 ? "" : "s"}? Existing variation edits in this field will be overwritten.`
      );
    },
    removeVariant(variant) {
      const variationIndex = this.variations.findIndex(
        (item) => item.id === variant.id
      );
      if (variationIndex === -1) return;
      this.revokePreview(this.variations[variationIndex].image_preview);
      this.variations.splice(variationIndex, 1);
      this.expandedVariations.splice(variationIndex, 1);
    },
    async createProduct() {
      if (this.processingForm) return;

      if (this.editBlockedReason) {
        return this.$toast.error(this.editBlockedReason);
      }

      if (this.editMode && !this.productKindConfirmed) {
        return this.$toast.error("The existing master product type could not be confirmed.");
      }

      if (!this.editMode) {
        if (!this.productKind) {
          return this.$toast.error("Choose a product type before entering product details.");
        }
        if (!this.itemName || !this.selectedCategory) {
          return this.$toast.error("Please enter name, and category!");
        }
      }

      if (!this.variations.length) {
        return this.$toast.error("Minimum 1 variation needed!");
      }
      const variationMandatoryFieldsMissing = this.variations.some(
        (item) => !item.name
      );
      if (variationMandatoryFieldsMissing) {
        return this.$toast.error("Please fillup mandatory variation fields!");
      }

      this.processingForm = true;

      try {
        await http.post("/items", this.buildProductFormData());
        this.$toast.success(
          this.editMode
            ? "Variants added successfully"
            : "Product created successfully!"
        );
        this.$router.push(
          "/products" + (this.editMode ? `/${this.productEditId}` : "")
        );
      } catch (err) {
        const message =
          err && err.response && err.response.data && err.response.data.message;
        this.$toast.error(message || "Unable to create product. Please try again.");
      } finally {
        this.processingForm = false;
      }
    },
    buildProductFormData() {
      const formData = new FormData();
      const fields = {
        edit_product_id: this.productEditId,
        product_kind: this.productKind,
        name: this.itemName,
        short_description: this.itemDescription,
        description: this.itemLongDescription,
        item_group: this.selectedCategory,
        manufacturer: this.manufacturerName,
        brand: this.brandName,
        strain: this.strainName,
        retail_marijuana_product_type__canada: this.isCannabis ? this.masterRetailMarijuanaProductType : "",
        equivalent_to: this.isCannabis ? this.masterEquivalentTo : "",
        health_canada_reporting_category: this.isCannabis ? this.masterHealthCanadaReportingCategory : "",
        terpenes: this.isCannabis ? this.masterTerpenes.join(",") : "",
        thc_min: this.isCannabis ? this.masterThcMin : "",
        thc_max: this.isCannabis ? this.masterThcMax : "",
        cbd_min: this.isCannabis ? this.masterCbdMin : "",
        cbd_max: this.isCannabis ? this.masterCbdMax : "",
      };
      Object.keys(fields).forEach((key) => formData.append(key, fields[key] == null ? "" : fields[key]));

      if (!this.editMode && this.masterImage) {
        formData.append("image", this.masterImage);
      }

      const variations = this.variations.map((variation) => {
        const payload = { ...variation };
        delete payload.id;
        delete payload.image_file;
        delete payload.image_preview;
        delete payload._batch_tracking_touched;
        delete payload.terpene_paste;
        payload.terpenes = this.isCannabis && Array.isArray(payload.terpenes)
          ? payload.terpenes.join(",")
          : "";
        if (variation.image_file) {
          payload.image_file_index = formData.getAll("variation_images").length;
          formData.append("variation_images", variation.image_file);
        }
        return payload;
      });
      formData.append("variations", JSON.stringify(variations));
      return formData;
    },
    async fetchCategories() {
      this.loading = true;
      try {
        const res = await http.get(`/item-groups`);
        this.categories = res.data.result;
      } catch (err) {
        console.log("error in loading items", err);
      }
      this.loading = false;
      this.categoriesLoaded = true;
    },
  },
};
</script>

<style lang="scss">
.custom-p {
  padding-top: 3px !important;
  padding-bottom: 3px !important;
  font-size: 11px;
}
.width8 {
  width: 80px;
}
</style>
<style lang="scss" scoped>
@import "../../assets/variables.scss";

.page-title {
  font-size: 1.5em;
  margin-bottom: $sectionGap * 2;
}
.section-title {
  font-size: 1.1rem;
}
.product-kind-step,
.master-asset,
.master-specifications,
.master-descriptions {
  border: 1px solid $lightBorderColor;
  border-radius: $cardRadius;
  background: $lightCardBg;
  padding: 18px;
}
.edit-blocked-message {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid rgba($dangerColor, 0.38);
  border-left: 4px solid $dangerColor;
  border-radius: $component-border-radius;
  background: rgba($dangerColor, 0.07);
  color: $textColor;

  i {
    margin-top: 3px;
    color: $dangerColor;
  }
  strong,
  span {
    display: block;
  }
  span {
    margin-top: 3px;
    line-height: 1.45;
  }
}
.product-workflow {
  margin-top: 22px;
}
.step-heading {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
}
.step-heading--details {
  padding-top: 4px;
}
.step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 30px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  color: white;
  background: $primaryColor;
  font-weight: 700;
}
.product-kind-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
.product-kind-card {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-height: 128px;
  padding: 16px;
  border: 1px solid $lightBorderColor;
  border-radius: $component-border-radius;
  background: rgba($primaryColor, 0.025);
  color: $textColor;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

  > i:first-child {
    margin-top: 3px;
    color: $primaryColor;
    font-size: 1.25rem;
  }
  strong,
  small {
    display: block;
  }
  strong {
    margin-bottom: 5px;
  }
  small {
    color: rgba($textColor, 0.72);
    line-height: 1.45;
  }
  &:hover,
  &:focus-visible {
    border-color: $primaryColor;
    box-shadow: 0 0 0 3px rgba($primaryColor, 0.13);
    outline: none;
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.62;
  }
  &:disabled:hover,
  &:disabled:focus-visible {
    border-color: $lightBorderColor;
    box-shadow: none;
  }
}
.product-kind-card--selected {
  border-color: $primaryColor;
  background: rgba($primaryColor, 0.08);
}
.selection-check {
  position: absolute;
  top: 10px;
  right: 10px;
  color: $primaryColor;
}
.selection-guidance {
  margin-top: 12px;
  padding: 10px 12px;
  border-left: 3px solid $primaryColor;
  background: rgba($primaryColor, 0.06);
  color: $textColor;
}
.asset-control {
  display: flex;
  align-items: center;
  gap: 18px;
}
.asset-control--compact {
  align-items: flex-start;
}
.asset-preview,
.asset-placeholder {
  flex: 0 0 112px;
  width: 112px;
  height: 112px;
  border: 1px solid $lightBorderColor;
  border-radius: $component-border-radius;
  background: rgba($primaryColor, 0.04);
}
.asset-preview {
  object-fit: cover;
}
.asset-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba($textColor, 0.45);
  font-size: 1.65rem;
}
.asset-copy {
  min-width: 0;
  flex: 1 1 auto;
  max-width: 100%;
}
.asset-copy .form-control-file {
  width: 100%;
  min-width: 0;
  max-width: 100%;
}
.file-label,
.subsection-title {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
}
.paste-label {
  display: block;
  margin-top: 12px;
  margin-bottom: 6px;
  font-weight: 500;
}
.paste-terpenes-btn {
  min-height: 36px;
  margin-top: 6px;
}
.terpene-token-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}
.terpene-token-summary span {
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba($primaryColor, 0.09);
  color: $textColor;
  font-size: 0.78rem;
}
.file-metadata {
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin-top: 8px;

  strong,
  span {
    overflow-wrap: anywhere;
  }
  span {
    color: rgba($textColor, 0.68);
    font-size: 0.82rem;
  }
}
.master-specifications,
.master-descriptions,
.master-asset {
  margin-top: 16px !important;
}
.potency-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}
.potency-grid label {
  font-weight: 500;
}
.potency-grid input {
  margin-top: 6px;
}
.spec-summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin: 0;
}
.spec-summary > div,
.readonly-behaviour {
  padding: 9px 11px;
  border: 1px solid $lightBorderColor;
  border-radius: $component-border-radius;
  background: rgba($primaryColor, 0.035);
}
.spec-summary dt {
  color: rgba($textColor, 0.64);
  font-size: 0.78rem;
}
.spec-summary dd {
  margin: 2px 0 0;
}
.variation-asset,
.cannabis-variation-specifications {
  padding: 14px;
  border: 1px solid $lightBorderColor;
  border-radius: $component-border-radius;
  background: rgba($primaryColor, 0.025);
}
.copy-description-btn {
  min-height: 36px;
  text-align: left;
  white-space: normal;
}
.tblec {
  font-size: 11px;
  th {
    padding: $tableCellPaddingY $tableCellPaddingX;
  }
  tr td {
    padding: $tableCellPaddingY $tableCellPaddingX;
  }
  .my-1 {
    margin-top: 0 !important;
  }
}
.table-head tr,
th {
  background-color: #777;
  color: white;
}

.variant-table-container input[type="text"],
.variant-table-container input[type="number"] {
  border: 1px solid $lightBorderColor;
  padding: 6px 8px;
  margin: 0;
  outline-color: $primaryColor;
  font-size: 12px;
  border-radius: $component-border-radius;
}
.variant-table-container {
  overflow-x: auto;
}
.link-btn {
  color: $infoColor;
}

button.link-btn {
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  font: inherit;
}

button.copy-description-btn {
  padding: 7px 10px;
  border: 1px solid currentColor;
  border-radius: $component-border-radius;
  font-weight: 600;
}

/* New styles for the accordion-style variation panels */
.product-variations {
  width: 100%;
}

.variation-panel {
  border: 1px solid $lightBorderColor;
  border-radius: $cardRadius;
  overflow: hidden;
  box-shadow: $cardShadow;
}

.variation-header {
  padding: $cardPaddingY $cardPaddingX;
  background-color: $lightCardBg;
  border-bottom: 1px solid $lightBorderColor;
  cursor: pointer;
}

.variation-content {
  padding: $cardPaddingY $cardPaddingX;
  background-color: $lightCardBg;
}

.create-product-page--dark {
  .product-kind-step,
  .master-asset,
  .master-specifications,
  .master-descriptions,
  .variation-panel,
  .variation-asset,
  .cannabis-variation-specifications,
  .spec-summary > div,
  .readonly-behaviour,
  .asset-preview,
  .asset-placeholder {
    border-color: $darkBorderColor;
  }

  .product-kind-step,
  .master-asset,
  .master-specifications,
  .master-descriptions,
  .variation-header,
  .variation-content {
    background: $darkCardBg;
    color: $darkTextColor;
  }

  .edit-blocked-message {
    border-color: rgba($dangerColor, 0.55);
    border-left-color: $dangerColor;
    background: rgba($dangerColor, 0.13);
    color: $darkTextColor;
  }

  .product-kind-card,
  .selection-guidance,
  .variation-asset,
  .cannabis-variation-specifications,
  .spec-summary > div,
  .readonly-behaviour,
  .asset-placeholder {
    background: $darkInputBg;
    border-color: $darkBorderColor;
    color: $darkTextColor;
  }

  .product-kind-card--selected {
    border-color: $secondaryColorDark;
    background: rgba($secondaryColorDark, 0.12);
  }

  .product-kind-card:disabled:hover,
  .product-kind-card:disabled:focus-visible {
    border-color: $darkBorderColor;
  }

  .product-kind-card > i {
    color: $secondaryColorDark;
  }

  .selection-check {
    color: $secondaryColorDark;
  }

  .product-kind-card small,
  .file-metadata span,
  .spec-summary dt,
  .text-muted {
    color: rgba($darkTextColor, 0.7) !important;
  }

  .terpene-token-summary span {
    background: rgba($secondaryColorDark, 0.2);
    color: $darkTextColor;
  }

  .selection-guidance {
    border-left-color: $secondaryColorDark;
  }

  .form-control {
    background: $darkInputBg;
    border-color: $darkBorderColor;
    color: $darkTextColor;
  }
}

:deep(.dark) {
  .page-title,
  .section-title,
  .master-descriptions label,
  .variation-content label,
  .input-label {
    color: $darkTextColor;
  }

  .master-descriptions .text-muted {
    color: rgba($darkTextColor, 0.7) !important;
  }

  .variation-panel {
    border-color: $darkBorderColor;
  }

  .variation-header,
  .variation-content {
    background-color: $darkCardBg;
    color: $darkTextColor;
  }

  .variation-header {
    border-bottom-color: $darkBorderColor;
  }

  .form-control {
    background-color: $darkInputBg;
    border-color: $darkBorderColor;
    color: $darkTextColor;

    &::placeholder {
      color: rgba($darkTextColor, 0.58);
    }

    &[readonly] {
      background-color: rgba($darkTextColor, 0.04);
    }
  }

  .custom-input {
    background-color: $darkInputBg;
    border-color: $darkBorderColor;
    color: $darkTextColor;

    &::placeholder {
      color: rgba($darkTextColor, 0.58);
    }
  }

  .link-btn {
    color: $secondaryColorDark;
  }
}

.ml-2 {
  margin-left: 0.5rem;
}

@media (max-width: 900px) {
  .product-kind-grid {
    grid-template-columns: 1fr;
  }
  .product-kind-card {
    min-height: 0;
  }
  .potency-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 575px) {
  .product-kind-step,
  .master-asset,
  .master-specifications,
  .master-descriptions,
  .variation-content {
    padding: 14px;
  }
  .asset-control {
    align-items: flex-start;
    flex-direction: column;
  }
  .asset-preview,
  .asset-placeholder {
    flex-basis: 96px;
    width: 96px;
    height: 96px;
  }
  .potency-grid,
  .spec-summary {
    grid-template-columns: 1fr;
  }
  .variation-header {
    align-items: flex-start !important;
    gap: 12px;
  }
}
</style>
